import { IAction } from '../root/i-action';
import { IState } from '../root/state.interface';
import {
  IAssetType, IAssetTypeProperties, IAssetTypes, IGetAssetsResponse,
} from './i-asset';
import { parseData } from '../../../utils/create-dataParser';

export const getAssetTypesListStart = (state: IState): IState => ({
  ...state,
  assetTypes: {
    data: [],
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const getAssetTypeStart = (state: IState): IState => ({
  ...state,
  assetType: {
    data: {
      id: 0,
      tenant: '',
      code: '',
      description: '',
      properties: [],
    },
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const getAssetTypeSuccess = (
  state: IState,
  action: IAction<IAssetType>,
): IState => ({
  ...state,
  assetType: {
    data: {
      id: action.payload.id,
      tenant: action.payload.tenant,
      code: action.payload.code,
      description: action.payload.description,
      properties: action.payload.properties,
    },
    loading: false,
    error: undefined,
    loaded: true,
  },
});

export const getAssetTypeFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetType: {
    data: {
      id: 0,
      tenant: '',
      code: '',
      description: '',
      properties: [],
    },
    loading: false,
    error: action.payload,
    loaded: true,
  },
});

export const getAssetTypesListSuccess = (
  state: IState,
  action: IAction<IGetAssetsResponse>,
): IState => {
  const data = parseData(action.payload);
  return ({
    ...state,
    assetTypes: {
      data,
      count: action.payload.count,
      fields: action.payload.fields,
      headers: action.payload.headers,
      loading: false,
      error: undefined,
      loaded: true,
    },
  });
};

export const getAssetTypesListFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetTypes: {
    data: [],
    loading: false,
    error: action.payload,
    loaded: true,
  },
});

export const editAssetStart = (state: IState): IState => ({
  ...state,
  assetTypes: {
    data: [...state.assetTypes.data],
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const editAssetSuccess = (
  state: IState,
  action: IAction<IAssetTypes>,
): IState => {
  const copyData = state.assetTypes.data;
  const index = copyData.findIndex((el) => el.id === action.payload.id);
  copyData[index] = action.payload;
  return ({
    ...state,
    assetTypes: {
      data: [...copyData],
      loading: false,
      error: undefined,
      loaded: true,
    },
  });
};

export const editAssetFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetTypes: {
    data: [],
    loading: false,
    error: action.payload,
    loaded: true,
  },
});

export const deleteAssetTypeStart = (state: IState): IState => ({
  ...state,
  assetTypes: {
    data: [...state.assetTypes.data],
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const deleteAssetTypeSuccess = (
  state: IState,
  action: IAction<number>,
): IState => {
  const copyData = state.assetTypes.data;
  const index = copyData.findIndex((el) => el.id === action.payload);
  copyData.splice(index, 1);
  return ({
    ...state,
    assetTypes: {
      data: [...copyData],
      loading: false,
      error: undefined,
      loaded: true,
    },
  });
};

export const deleteAssetTypeFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetTypes: {
    data: [],
    loading: false,
    error: action.payload,
    loaded: true,
  },
});

export const getAssetTypePropertiesStart = (state: IState): IState => ({
  ...state,
  assetTypeProperties: {
    data: [],
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const getAssetTypePropertiesSuccess = (
  state: IState,
  action: IAction<Array<IAssetTypeProperties>>,
): IState => ({
  ...state,
  assetTypeProperties: {
    data: action.payload,
    loading: false,
    error: undefined,
    loaded: true,
  },
});

export const getAssetTypePropertiesFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetTypeProperties: {
    data: [],
    loading: false,
    error: action.payload,
    loaded: true,
  },
});

export const editAssetTypePropertyStart = (state: IState): IState => ({
  ...state,
  assetTypeProperties: {
    data: [...state.assetTypeProperties.data],
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const editAssetTypePropertySuccess = (
  state: IState,
  action: IAction<IAssetTypeProperties>,
): IState => {
  const copyData = state.assetTypeProperties.data.map((assetTypeProperty) => {
    if (assetTypeProperty.id === action.payload.id) {
      return { ...assetTypeProperty, ...action.payload };
    }
    return assetTypeProperty;
  });
  return ({
    ...state,
    assetTypeProperties: {
      data: [...copyData],
      loading: false,
      error: undefined,
      loaded: true,
    },
  });
};

export const editAssetTypePropertyFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetTypeProperties: {
    data: [],
    loading: false,
    error: action.payload,
    loaded: true,
  },
});

export const addAssetTypePropertyStart = (state: IState): IState => ({
  ...state,
  assetTypeProperties: {
    data: [...state.assetTypeProperties.data],
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const addAssetTypePropertySuccess = (
  state: IState,
  action: IAction<IAssetTypeProperties>,
): IState => {
  const copyData = state.assetTypeProperties.data.map((assetTypeProperty) => {
    if (assetTypeProperty.id === action.payload.id) {
      return { ...assetTypeProperty, ...action.payload };
    }
    return assetTypeProperty;
  });
  return ({
    ...state,
    assetTypeProperties: {
      data: [...copyData],
      loading: false,
      error: undefined,
      loaded: true,
    },
  });
};

export const addAssetTypePropertyFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetTypeProperties: {
    data: [],
    loading: false,
    error: action.payload,
    loaded: true,
  },
});

export const deleteAssetTypePropertyStart = (state: IState): IState => ({
  ...state,
  assetTypeProperties: {
    data: [...state.assetTypeProperties.data],
    loading: true,
    error: undefined,
    loaded: false,
  },
});

export const deleteAssetTypePropertySuccess = (
  state: IState,
  action: IAction<Array<number>>,
): IState => {
  const copyData = state.assetTypeProperties.data;
  action.payload.forEach((id) => {
    const index = copyData.findIndex((el) => el.id === id);
    copyData.splice(index, 1);
  });
  return ({
    ...state,
    assetTypeProperties: {
      data: [...copyData],
      loading: false,
      error: undefined,
      loaded: true,
    },
  });
};

export const deleteAssetTypePropertyFail = (
  state: IState,
  action: IAction<Error>,
): IState => ({
  ...state,
  assetTypeProperties: {
    data: [],
    loading: false,
    error: action.payload,
    loaded: true,
  },
});
